// [목적] /chat 좌측 "여행 설계 가이드" 단계 정의 단일 소스 — 질문/보기/안내 문구 + 최종 문장 조립.
// [이유] 2026-09-21 trip-guide: 첫 진입 화면이 "자유 입력 + 추천 문구 5개" 뿐이라 무엇을 어디까지 말해야
//        하는지 알 수 없었다(사용자 피드백: "방향성이 없어서 쓰기 힘들 것 같다"). 대화 분기형으로
//        지역 → 동반자/인원 → 이동 배려 → 기간 → 무드 → 예산 순으로 물어보고, 답을 모아 한 문장으로
//        조립해 기존 입력창에 넣어준다(전송 로직은 건드리지 않음 — useChatFlow.send 그대로 사용).
// [사이드 이팩트] 없음(순수 데이터/함수). UI 는 TripGuide.tsx, 우측 미리보기는 GuidePreview.tsx.
// [수정 시 고려사항] 모든 단계에 note(안내)를 둔다 — 보기에 없으면 채팅으로 유도하는 것이 이 화면의 핵심 UX다.
//                   보기 문구를 바꾸면 buildGuideSentence 의 분기(‘아직 못 정했어요’, ‘해당 없음’)도 함께 볼 것.

// [2026-09-21] 'when'(출발 날짜) · 'transport'(이동수단) · 'range'(이동 반경) 3단계 추가.
// [이유] 세 값 모두 BE 슬롯으로 이미 받고 있는데(startDate/transport/rangePreference) 가이드에는 없어서,
//        가이드로 시작한 사용자는 챗 되묻기로 다시 답해야 했다. 특히 이동수단은 하드필수라 반드시 되물린다.
//        날짜는 요일(주말 혼잡)·혼잡도 예측과, 이동수단/반경은 후보 장소 선정과 직결된다.
export type GuideStepKey =
  | 'region'
  | 'who'
  | 'care'
  // [2026-09-21] when(출발 날짜) + days(기간)를 'when' 한 단계로 합쳤다.
  // [이유] "언제 떠나서 며칠 있다 오는지"는 하나의 일정 판단이다. 따로 물으면 단계만 늘고,
  //        날짜를 고른 직후 기간을 고르는 흐름이 끊긴다(move 단계와 같은 이유).
  //        답변은 when(날짜) / days(기간) 두 필드에 그대로 분리 보관한다.
  | 'when'
  // [2026-09-21] transport·range 를 'move' 한 단계로 합쳤다.
  // [이유] 두 질문은 "어떻게 이동하고 얼마나 멀리 갈지"라는 하나의 판단이라, 따로 물으면 단계만
  //        늘고(9단계) 맥락이 끊긴다. 특히 반경은 이동수단을 봐야 정할 수 있어서 같은 화면에
  //        나란히 두는 편이 고르기 쉽다. 답변(transport/range)은 그대로 분리 보관한다.
  | 'move'
  | 'mood'
  | 'budget'
  // [2026-09-21] 마지막 확인 단계 — "디테일하게 넣고 싶은 게 있는지" 자유 입력.
  // [이유] 예전에는 이 질문을 BE 가 채팅 버블("더 추가하고 싶은 여행 요소가 있으신가요?")로 던졌는데,
  //        가이드로 여기까지 온 사용자에게 갑자기 채팅 화면이 열리는 게 흐름을 깬다(사용자 요청: 채팅 제거).
  //        같은 질문을 가이드 마지막 단계로 가져와 한 화면에서 끝낸다.
  | 'detail';

/** 사용자가 지금까지 고른 값 — TripGuide/GuidePreview 가 공유한다 */
export interface GuideAnswers {
  region?: string;
  who?: string;
  /** 본인 포함 인원(반려견 동반이면 사람 수) */
  count?: number;
  /**
   * 이동 배려 조건(복수 선택). 빈 배열 = '해당 없어요'(답변함), undefined = 아직 안 물음.
   * [2026-09-21 수정] 과거에는 '·' 로 이어붙인 문자열이었는데, 보기 자체에 '·' 가 들어간
   * '엘리베이터·경사로' 가 두 개로 쪼개져 미리보기 타일이 잘려 나왔다 — 배열로 보관한다.
   */
  care?: string[];
  days?: string;
  /** 무드(최대 MOOD_MAX개) — care 와 같은 이유로 배열 */
  mood?: string[];
  /** 1인 기준 예산(만원). 제한 없음이면 null */
  budgetPer?: number | null;
  /** 출발 날짜 'YYYY-MM-DD'. '아직 미정'이면 null(답변함), undefined = 아직 안 물음 */
  when?: string | null;
  /** 이동수단 — BE TransportEnum 라벨과 동일 문자열 */
  transport?: string;
  /** 마지막 자유 요청. null = '없어요'(답변함), undefined = 아직 안 물음 */
  detail?: string | null;
  /**
   * 이동 반경(km). BE rangePreference 슬롯과 동일 계약 —
   * 0 = 거리 제한 없음, undefined = 아직 안 고름.
   * [2026-09-21] 칩 4종 → 슬라이더+직접입력으로 바꾸면서 문자열 라벨에서 숫자로 교체(예산과 동일 조작).
   */
  rangeKm?: number;
}

export interface GuideOption {
  value: string;
  /** 보기 아래 작은 보조 설명(예: '바다+카페') */
  sub?: string;
}

export interface GuideStep {
  key: GuideStepKey;
  question: string;
  /** 질문 옆 작은 뱃지(예: '1인 기준') */
  badge?: string;
  hint: string;
  /** "보기에 없으면 직접 입력" 안내 — 모든 단계 필수 */
  note: string;
  /** '직접 입력하기' 입력창 placeholder. 없으면 직접 입력 자체를 제공하지 않는다(예산은 전용 위젯) */
  customPlaceholder?: string;
  /** 복수 선택 단계 여부 */
  multi?: boolean;
  /** 복수 선택 단계에서 '해당 없어요' 스킵 허용 */
  optional?: boolean;
  /** 보기 대신 전용 위젯을 그리는 단계 */
  custom?: 'budget' | 'when' | 'move' | 'detail';
  /** 앞 단계 답에 따라 보기가 달라질 수 있어 함수도 허용 */
  options: GuideOption[] | ((answers: GuideAnswers) => GuideOption[]);
}

/** 무드 최대 선택 수 */
export const MOOD_MAX = 3;
/** 인원 입력 범위(본인 포함) */
export const COUNT_MIN = 1;
export const COUNT_MAX = 10;
/** 예산 슬라이더 범위(1인 기준, 만원) */
export const BUDGET_MIN = 5;
export const BUDGET_MAX = 100;
export const BUDGET_STEP = 5;
/** 직접 입력 허용 범위 — 슬라이더 밖 금액도 타이핑으로 넣을 수 있다 */
export const BUDGET_INPUT_MAX = 300;
export const BUDGET_DEFAULT = 25;

export const GUIDE_STEPS: readonly GuideStep[] = [
  {
    key: 'region',
    question: '어디로 떠나볼까요?',
    hint: '딱 한 곳만 골라주세요. 나중에 바꿀 수 있어요.',
    note: '마음에 드는 곳이 없나요? 보기 끝의 ‘직접 입력하기’를 눌러 적어주세요 — 예: “인제 자작나무숲”',
    customPlaceholder: '예: 인제 자작나무숲',
    options: [
      { value: '강릉', sub: '바다+카페' },
      { value: '속초', sub: '해변+설악' },
      { value: '양양', sub: '서핑' },
      { value: '평창', sub: '산·목장' },
      { value: '춘천', sub: '호수·닭갈비' },
      { value: '정선', sub: '계곡·레일바이크' },
      { value: '아직 못 정했어요', sub: '추천받기' },
    ],
  },
  {
    key: 'who',
    question: '누구와 함께 가나요?',
    hint: '동반자에 따라 코스 난이도와 동선이 달라져요.',
    note: '인원은 본인을 포함해서 세주세요 — “가족 3명”이면 나까지 3명이에요. 보기에 없는 관계는 ‘직접 입력하기’로 적어주세요 — 예: “회사 동료와”',
    customPlaceholder: '예: 회사 동료와',
    options: [
      { value: '혼자' },
      { value: '연인과' },
      { value: '친구와' },
      { value: '가족과' },
      { value: '반려견과' },
      { value: '어르신·아이와' },
    ],
  },
  {
    key: 'care',
    question: '이동할 때 필요한 배려가 있나요?',
    hint: '해당되는 것만 골라주세요. 코스에서 접근이 어려운 곳은 빼드려요.',
    note: '휠체어·유아차 이용, 반려동물 동반처럼 꼭 필요한 조건이 있으면 알려주세요. 목록에 없으면 ‘직접 입력하기’로 추가할 수 있어요 — 예: “계단 없는 곳만”',
    customPlaceholder: '예: 계단 없는 곳만',
    multi: true,
    optional: true,
    // 반려견 동반이면 반려견 조건을 앞으로 당겨 보여준다(필요한 사람에게 먼저 보이게)
    options: (answers) =>
      answers.who === '반려견과'
        ? [
            { value: '반려견 동반 가능', sub: '실내 포함' },
            { value: '대형견 가능' },
            { value: '리드줄 산책로' },
            { value: '휠체어 접근' },
            { value: '유아차 이동' },
            { value: '무장애 화장실' },
            { value: '장애인 주차' },
          ]
        : [
            { value: '휠체어 접근' },
            { value: '유아차 이동' },
            { value: '무장애 화장실' },
            { value: '장애인 주차' },
            { value: '엘리베이터·경사로' },
            { value: '반려견 동반 가능' },
            { value: '대형견 가능' },
          ],
  },
  {
    key: 'when',
    question: '언제, 며칠 떠나시나요?',
    hint: '날짜를 정하면 요일까지 맞춰 붐비는 곳을 피하고, 기간에 맞춰 Day 카드를 만들어요.',
    note: '날짜가 아직이면 ‘아직 미정’으로 넘어가도 괜찮아요. 4박 이상이면 기간에서 ‘직접 입력’을 눌러주세요.',
    custom: 'when',
    options: [],
  },
  {
    key: 'move',
    question: '어떻게, 어디까지 다닐까요?',
    hint: '이동수단에 따라 갈 수 있는 거리가 달라져요.',
    note: '보기에 없는 방법(예: 오토바이, 자전거)이 있다면 채팅으로 알려주세요.',
    custom: 'move',
    options: [],
  },
  {
    key: 'mood',
    question: '어떤 무드가 끌리세요?',
    hint: `최대 ${MOOD_MAX}개까지 고르고 '다 골랐어요'를 눌러주세요.`,
    note: '원하는 분위기가 목록에 없다면 ‘직접 입력하기’로 추가해주세요 — 예: “야경 좋은 곳”',
    customPlaceholder: '예: 야경 좋은 곳',
    multi: true,
    options: [
      { value: '바다' },
      { value: '카페' },
      { value: '맛집' },
      { value: '캠핑' },
      { value: '액티비티' },
      { value: '힐링' },
      { value: '포토스팟' },
      { value: '축제' },
    ],
  },
  {
    key: 'budget',
    question: '예산은 어느 정도로?',
    badge: '1인 기준',
    hint: '슬라이더를 끌거나 금액을 직접 입력해보세요. 인원수에 맞춰 총 예산이 바로 계산돼요.',
    note: '금액은 숫자를 직접 입력할 수 있어요. 슬라이더 범위를 벗어난 금액도 그대로 넣어주세요.',
    custom: 'budget',
    options: [],
  },
  {
    key: 'detail',
    question: '마지막으로, 꼭 넣고 싶은 게 있나요?',
    hint: '특정 장소, 꼭 먹고 싶은 음식, 피하고 싶은 것 등 무엇이든 적어주세요.',
    note: '없으면 ‘없어요’를 눌러 바로 만들 수 있어요.',
    custom: 'detail',
    options: [],
  },
];

/** 마지막 단계 예시 칩 — 무엇을 적으면 되는지 보여주는 용도(누르면 입력창에 채워진다) */
export const DETAIL_EXAMPLES: ReadonlyArray<string> = [
  '주문진 수산시장 꼭 가고 싶어요',
  '회는 빼주세요',
  '아침은 숙소에서 간단히',
  '사람 많은 곳은 피하고 싶어요',
];

/** 기간 보기 — when 단계 위젯이 소비 */
export const DAYS_OPTIONS: ReadonlyArray<string> = ['당일치기', '1박 2일', '2박 3일', '3박 4일'];

/** 이동수단 보기 — BE TransportEnum 라벨과 동일 문자열이어야 한다(문장 조립 → 슬롯 추출 경로) */
export const TRANSPORT_OPTIONS: ReadonlyArray<GuideOption> = [
  { value: '자가용', sub: '내 차로' },
  { value: '렌터카', sub: '현지에서 빌려요' },
  { value: '기차', sub: 'KTX·무궁화' },
  { value: '시외버스', sub: '고속·시외' },
  { value: '대중교통', sub: '아직 미정' },
];

/** 자차 동선 여부 — BE isSelfDrivingTransport 와 같은 기준 */
export function isSelfDriveLabel(transport: string | undefined): boolean {
  return transport === '자가용' || transport === '렌터카';
}

/** 거리 제한 없음 센티널 — BE RANGE_UNLIMITED 와 동일 */
export const RANGE_UNLIMITED = 0;
/** 슬라이더 범위(km) */
export const RANGE_SLIDER_MIN = 1;
export const RANGE_SLIDER_MAX = 60;
export const RANGE_SLIDER_STEP = 1;
/** 직접 입력 허용 상한 — 슬라이더 밖 값도 타이핑으로 넣을 수 있다(예산 위젯과 동일 정책) */
export const RANGE_INPUT_MAX = 200;

/**
 * [목적] 반경 퀵 버튼 — 이동수단에 따라 설명이 달라진다
 * [이유] 같은 30km 라도 자차는 '시군 전체', 버스는 '환승 각오'다. 숫자만으로는 감이 안 온다.
 */
export function rangePresets(
  transport: string | undefined,
): ReadonlyArray<{ label: string; km: number; sub: string }> {
  const selfDrive = isSelfDriveLabel(transport);
  return [
    { label: '거점 주변', km: 5, sub: '걸어서' },
    { label: '시내권', km: 15, sub: '가깝게' },
    { label: '시군 전체', km: 30, sub: selfDrive ? '무난해요' : '환승 각오' },
    { label: '넓게', km: 60, sub: selfDrive ? '어디든' : '이동 김' },
  ];
}

/** 반경 값 → 표시 문구 (요약칩·미리보기·문장 조립 공용) */
export function formatRangeKm(km: number | undefined): string {
  if (km === undefined) return '';
  return km === RANGE_UNLIMITED ? '거리 상관없음' : `${km}km 이내`;
}

/** 예산 퀵 버튼 — 슬라이더/입력과 같은 값에 연동 */
export const BUDGET_PRESETS: ReadonlyArray<{ label: string; value: number }> = [
  { label: '가볍게', value: 10 },
  { label: '적당히', value: 25 },
  { label: '넉넉하게', value: 45 },
  { label: '럭셔리', value: 70 },
];

export type BudgetTier = 'light' | 'normal' | 'rich' | 'luxury';

/** 1인 금액 → 등급 라벨 (슬라이더를 끌 때 실시간 표시) */
export function budgetTier(perPerson: number): { tier: BudgetTier; label: string } {
  if (perPerson < 15) return { tier: 'light', label: '가볍게' };
  if (perPerson < 35) return { tier: 'normal', label: '적당히' };
  if (perPerson < 60) return { tier: 'rich', label: '넉넉하게' };
  return { tier: 'luxury', label: '럭셔리' };
}

/**
 * [목적] 'YYYY-MM-DD' → '3월 5일(목)' 표시용 변환
 * [이유] 요약칩·미리보기에 ISO 문자열을 그대로 보여주면 읽기 어렵고 요일도 안 보인다.
 *        요일은 이 화면에서 특히 중요한 정보다(주말이면 붐빈다).
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] 'YYYY-MM-DD' 를 new Date() 에 그대로 넣으면 UTC 로 해석돼 하루 밀린다 — 분해해서 만든다.
 */
const GUIDE_WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

export function formatGuideDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${d.getMonth() + 1}월 ${d.getDate()}일(${GUIDE_WEEKDAY[d.getDay()]})`;
}

/** 단계별 보기 해석 — options 가 함수인 경우를 흡수 */
export function resolveOptions(step: GuideStep, answers: GuideAnswers): GuideOption[] {
  return typeof step.options === 'function' ? step.options(answers) : step.options;
}

/** 요약칩에 보여줄 표시 문자열(인원/예산은 값만으로는 설명이 부족해 별도 조립) */
export function answerLabel(key: GuideStepKey, answers: GuideAnswers): string {
  if (key === 'who') {
    if (!answers.who) return '';
    return answers.count ? `${answers.who} · ${answers.count}명` : answers.who;
  }
  if (key === 'budget') {
    if (answers.budgetPer == null) return '상관없어요';
    const total = answers.budgetPer * (answers.count ?? 1);
    return `1인 ${answers.budgetPer}만원 · 총 ${total.toLocaleString('ko-KR')}만원`;
  }
  if (key === 'care') return answers.care?.length ? answers.care.join(' · ') : '해당 없어요';
  if (key === 'detail') return answers.detail === null ? '없어요' : (answers.detail ?? '');
  if (key === 'move') {
    if (!answers.transport) return '';
    return answers.rangeKm !== undefined
      ? `${answers.transport} · ${formatRangeKm(answers.rangeKm)}`
      : answers.transport;
  }
  if (key === 'when') {
    const datePart = answers.when === null ? '날짜 미정' : formatGuideDate(answers.when ?? '');
    return answers.days ? `${datePart} · ${answers.days}` : datePart;
  }
  if (key === 'mood') return answers.mood?.join(' · ') ?? '';
  return answers[key] ?? '';
}

/** 단계 답변이 채워졌는지 — 진행 게이지/미리보기 카드 활성 판정 공용 */
export function isAnswered(key: GuideStepKey, answers: GuideAnswers): boolean {
  if (key === 'budget') return answers.budgetPer !== undefined;
  // when 은 null('아직 미정')도 '답변함'이다 — care 와 동일 규칙
  // when 은 날짜와 기간을 모두 답해야 완료 — 날짜는 null('아직 미정')도 답변으로 인정한다
  if (key === 'when') return answers.when !== undefined && Boolean(answers.days);
  // detail 은 null('없어요')도 답변으로 인정한다
  if (key === 'detail') return answers.detail !== undefined;
  // move 는 이동수단과 반경을 모두 골라야 완료 — 반경은 수단을 봐야 정할 수 있다
  if (key === 'move') return Boolean(answers.transport) && answers.rangeKm !== undefined;
  // care 는 빈 배열('해당 없어요')도 '답변함'이다
  if (key === 'care') return Array.isArray(answers.care);
  if (key === 'mood') return (answers.mood?.length ?? 0) > 0;
  return Boolean(answers[key]);
}

/**
 * [목적] 고른 값들을 자연어 한 문장으로 조립 — 그대로 채팅 입력창에 들어간다.
 * [이유] BE 계약은 "자연어 한 줄"이므로(useChatFlow.send) 구조화 슬롯을 새로 만들지 않고 문장으로 넘긴다.
 * [수정 시 고려사항] 보기 문구를 바꾸면 여기 예외 분기('아직 못 정했어요')도 함께 갱신할 것.
 */
export function buildGuideSentence(answers: GuideAnswers): string {
  const region = answers.region === '아직 못 정했어요' ? '강원 어디든' : (answers.region ?? '');
  const party =
    answers.count === 1
      ? '혼자'
      : answers.count
        ? `${answers.who ?? ''} 총 ${answers.count}명이서`
        : (answers.who ?? '');

  // [2026-09-21] 날짜·이동수단·반경을 문장에 싣는다 — BE 는 자연어 한 줄에서 슬롯을 추출하므로
  //              여기서 빠지면 가이드로 고른 값이 그대로 버려진다.
  const whenPart = answers.when ? `${formatGuideDate(answers.when)}부터 ` : '';
  let sentence = `${whenPart}${region} ${answers.days ?? ''} 여행, ${party} 가는데 `;
  if (answers.transport) sentence += `${answers.transport}(으)로 이동하고 `;
  if (answers.rangeKm !== undefined) {
    sentence +=
      answers.rangeKm === RANGE_UNLIMITED
        ? '거리는 상관없고 '
        : `거점에서 ${answers.rangeKm}km 이내에서 `;
  }
  if (answers.mood?.length) sentence += `${answers.mood.join(', ')} 위주로 `;
  if (answers.care?.length) sentence += `${answers.care.join(', ')} 가능한 곳으로 `;
  if (answers.budgetPer) {
    const total = answers.budgetPer * (answers.count ?? 1);
    sentence += `예산은 1인 ${answers.budgetPer}만원(총 ${total}만원)으로 `;
  }
  // [2026-09-21] 마지막 자유 요청을 문장 끝에 덧붙인다 — BE 는 자연어 한 줄에서 슬롯을 뽑으므로
  //              여기서 빠지면 사용자가 적은 디테일이 그대로 버려진다.
  //              detail 이 없으면 문장을 그대로 둔다(마침표도 붙이지 않는다 — 기존 문장 계약 유지).
  const core = `${sentence.replace(/\s+/g, ' ').trim()} 짜줘`;
  return answers.detail ? `${core}. 그리고 ${answers.detail}` : core;
}
